// Pricing migration 18 (ADO-2249): what a priced product says about its price, as pure
// functions every surface shares.
//
// - The kind (AC1): only a promotion and a contract are named. A WOW price reads as a standard
//   one (DEC-9: the WOW entry already says so), and a standard price carries no label.
// - The saving (AC2): the was price minus the unit price, per case, in whole cents, and only
//   when the quote has a was price. A promotion that beat an absolute contract has none.
// - The offer text: the designer's copy word for word (Figma set 1, the v9 proposal board), with
//   each part the quote lacks left out (Clarifications): no `Save €X` without a was price, no
//   `Ends …` without an end date. No promotion name (DEC-7).
// - What screen readers hear (AC3): DEC-15's table, so a struck-through number is never the
//   only way the was price is told.
// - The was RSP (Pricing migration 37, ADO-4034): the RSP before the promotion, struck before the
//   RSP, and its spoken text, which follows DEC-15 the same way.
//
// The web computes no price here: the saving is the difference of two figures the endpoint
// sent, as the basket sums its stored figures (`cartTotals.ts`).

import { toCents } from '../basket/cartTotals';
import { formatDayMonth } from '../shared/dayMonth';
import { formatMoney, type MoneyValue } from '../shared/money';
import type { PriceEntry } from './usePriceStore';

/** A SKU the endpoint priced. */
export type PricedEntry = Extract<PriceEntry, { readonly state: 'priced' }>;

/** A priced entry with a figure `ProductPrice` will show (it hides a zero one), else `null`. */
export function shownEntry(entry: PriceEntry | undefined): PricedEntry | null {
  return entry?.state === 'priced' && entry.unitPrice.amount > 0 ? entry : null;
}

/** True when a promotion set the price: the only kind shown in red, with an offer row. */
export function isPromotion(entry: PricedEntry): boolean {
  return entry.kind === 'promotion';
}

/**
 * The per-case saving, `wasPrice − unitPrice` in whole cents, or `null` when there is none to
 * show: not a promotion (only a promotion has a saving, DEC-6), no was price, a was price in
 * another currency, or one that is not above the price (a saving of nothing or less is never
 * shown).
 */
export function promotionSaving(entry: PricedEntry): MoneyValue | null {
  const { wasPrice, unitPrice } = entry;
  if (!isPromotion(entry) || wasPrice === undefined || wasPrice.currency !== unitPrice.currency)
    return null;
  const cents = toCents(wasPrice.amount) - toCents(unitPrice.amount);
  if (!(cents > 0)) return null;
  return { amount: cents / 100, currency: unitPrice.currency };
}

/**
 * A promotion's end date as day and short month, no year (`11 Nov`), or `null` when it is not a
 * real `YYYY-MM-DD` date: the shared reading in `shared/dayMonth.ts`, which multi-buy deal dates
 * use too (ADO-3985).
 */
export const formatPromotionEndDate = formatDayMonth;

/** The parts of a promotion's offer text the quote supports: the saving and the end date. */
function offerParts(entry: PricedEntry): { saving: string | null; ends: string | null } {
  const saving = promotionSaving(entry);
  const endDate =
    entry.promotionEndDate === undefined ? null : formatPromotionEndDate(entry.promotionEndDate);
  return {
    saving: saving === null ? null : formatMoney(saving),
    ends: endDate === null ? null : `Ends ${endDate}`,
  };
}

/**
 * The grid card's offer row: `Save €4.86 · Ends 11 Nov`, or `Ends 11 Nov` with no was price.
 * `null` for any kind but a promotion, and for a promotion with nothing to say.
 */
export function gridOfferText(entry: PricedEntry): string | null {
  if (!isPromotion(entry)) return null;
  const { saving, ends } = offerParts(entry);
  const parts = [saving === null ? null : `Save ${saving}`, ends].filter(
    (part): part is string => part !== null,
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

/** Options for {@link listOfferText}. */
export interface ListOfferTextOptions {
  /** `false` drops the leading `Promotion`: the product page's strip below 1024px (Pricing
   *  migration 32, ADO-3985, frames t/04 and m/04). Default `true`. */
  readonly prefix?: boolean;
}

/**
 * The list card's offer pill and the product page's offer strip:
 * `Promotion · Save €4.86 a case · Ends 11 Nov`, or `Promotion · Ends 11 Nov` with no was
 * price. `null` for any kind but a promotion.
 *
 * With `prefix: false` (frames t/04 and m/04): `Save €4.86 a case · Ends 11 Nov`, keeping
 * "a case". The prefix is dropped only when something follows it, so a promotion with neither a
 * saving nor an end date still reads `Promotion`, as it does at every width today.
 */
export function listOfferText(
  entry: PricedEntry,
  { prefix = true }: ListOfferTextOptions = {},
): string | null {
  if (!isPromotion(entry)) return null;
  const { saving, ends } = offerParts(entry);
  const details = [saving === null ? null : `Save ${saving} a case`, ends].filter(
    (part): part is string => part !== null,
  );
  if (!prefix && details.length > 0) return details.join(' · ');
  return ['Promotion', ...details].join(' · ');
}

/**
 * The RSP the product had before its promotion, to show struck before the RSP (ADO-4034), or
 * `null` when there is none to show: no was RSP, no RSP shown beside it (AC3), a was RSP in
 * another currency than the RSP, or a figure that is not above zero. The web trusts the API's
 * presence rule (DEC-6): the endpoint sends it only on a promotion whose RSP differs, so there is
 * no kind check and no "higher than the RSP" check here.
 */
export function shownWasRrp(entry: PricedEntry): MoneyValue | null {
  const { rrp, wasRrp } = entry;
  if (rrp === undefined || wasRrp === undefined) return null;
  if (!(rrp.amount > 0) || !(wasRrp.amount > 0) || wasRrp.currency !== rrp.currency) return null;
  return wasRrp;
}

/** `RSP was €2.80, now €2.50` from the formatted RSP and was RSP (ADO-4034 AC4, DEC-7). */
export function spokenRsp(rrp: string, wasRrp: string): string {
  return `RSP was ${wasRrp}, now ${rrp}`;
}

/** `Was €5.49, now €4.18`, with `suffix` after the price (` per case` on a cart line). */
export function spokenWasNow(wasPrice: MoneyValue, price: MoneyValue, suffix = ''): string {
  return `Was ${formatMoney(wasPrice)}, now ${formatMoney(price)}${suffix}`;
}

/**
 * What a screen reader hears for the price (DEC-15): `Promotion. Was €21.65, now €16.79`,
 * `Promotion. €16.79` with no was price, `Contract price. €8.75`, and the price alone for a
 * standard or WOW price.
 */
export function spokenPrice(entry: PricedEntry): string {
  const price = formatMoney(entry.unitPrice);
  if (isPromotion(entry)) {
    const wasPrice = promotionSaving(entry) === null ? undefined : entry.wasPrice;
    return wasPrice === undefined
      ? `Promotion. ${price}`
      : `Promotion. ${spokenWasNow(wasPrice, entry.unitPrice)}`;
  }
  if (entry.kind === 'contract') return `Contract price. ${price}`;
  return price;
}

/** What the card and the product page draw for a priced entry (ADO-2249, Task 3; DEC-15). */
export interface ExplainedPrice {
  /** The price is red. */
  readonly promotion: boolean;
  /** The `Contract price` pill shows. */
  readonly contract: boolean;
  /** The was price struck beside the price: a promotion's, above the price only. */
  readonly wasPrice: MoneyValue | undefined;
  /** What a screen reader hears instead of the figures, or `null` to read the figure itself. */
  readonly spoken: string | null;
  /** `+ €3.00 deposit`, or `null` when the deposit is zero. */
  readonly deposit: string | null;
}

export function explainedPrice(entry: PricedEntry): ExplainedPrice {
  const promotion = isPromotion(entry);
  const contract = entry.kind === 'contract';
  return {
    promotion,
    contract,
    wasPrice: promotionSaving(entry) === null ? undefined : entry.wasPrice,
    // Only a promotion or a contract says more than its figure.
    spoken: promotion || contract ? spokenPrice(entry) : null,
    deposit: entry.deposit.amount > 0 ? `+ ${formatMoney(entry.deposit)} deposit` : null,
  };
}
